import type { HostedAgentSession } from "@/api/client/hosted-agents"
import type { AgentRunModel } from "@/api/client/messages"
import type { ActivityMessage } from "@/api/models"
import type { SessionState } from "@/stores/slices/chat-slice"

import { durableTurnErrorText } from "@/features/chat/utils/durable-turn-error-i18n"
import { getSessionErrorCode } from "@/features/chat/utils/session-error-code"

export type AgentPresence =
  | "idle"
  | "thinking"
  | "working"
  | "waiting"
  | "blocked"
  | "done"
  | "stopped"
  | "stopping"
  | "reconnecting"
  | "loading"

/** The three flavours of "working" the character can act out (owner ask 2026-09-13:
 * running code, writing, reading should look different). Derived from the tool
 * the latest activity names; anything else keeps the generic working bounce. */
export type WorkingActivityKind = "code" | "read" | "write"

const CODE_TOOLS = new Set(["run_python", "run_shell", "document_code_runner", "pdf_code_runner"])
const READ_TOOLS = new Set([
  "source_read",
  "source_search",
  "library_search",
  "file_search",
  "web_search",
  "web_scrape",
  "doc_content_reader",
  "tender_case_reader",
  "microsoft_365_search",
  "load_skill",
])
const WRITE_TOOLS = new Set([
  "deliver_workspace_file",
  "doc_creator",
  "doc_updater",
  "agent_working_state",
])

export function workingActivityKind(
  tool: string | undefined,
  action?: string
): WorkingActivityKind | null {
  if (!tool) return null
  if (tool === "workspace_files") {
    if (action === "write" || action === "delete") return "write"
    if (action === "read" || action === "list" || action === "import_sources") return "read"
    return null
  }
  if (CODE_TOOLS.has(tool)) return "code"
  if (READ_TOOLS.has(tool)) return "read"
  if (WRITE_TOOLS.has(tool)) return "write"
  return null
}

export function workingActivityKindFromMessage(
  activity: ActivityMessage | undefined
): WorkingActivityKind | null {
  return workingActivityKind(
    typeof activity?.details?.tool === "string" ? activity.details.tool : undefined,
    typeof activity?.details?.action === "string" ? activity.details.action : undefined
  )
}

/** Live activity for the presence line.
 *
 * While `streamingAnswer` is set, that answer's last activity. Otherwise the
 * last message's last activity. A missing list yields no activity.
 */
export function latestAnswerActivity(
  streamingAnswer: SessionState["streamingAnswer"],
  messages: SessionState["messages"]
): ActivityMessage | undefined {
  const answer = streamingAnswer ? streamingAnswer[1] : messages.at(-1)?.[1]
  const activities = answer?.activities
  if (!activities?.length) return undefined
  return activities[activities.length - 1]
}

/** Identity is tied to the immutable agent id, never its position or editable name. */
export function agentCharacter(id: string): number {
  let hash = 0
  for (const character of id) hash = (Math.imul(hash, 31) + character.charCodeAt(0)) | 0
  return (hash >>> 0) % 4
}

/** The roster has no agent-wide presence API. Surface only positively matched active
 * runs in the caller's own threads, never infer availability from missing data. */
export function agentRosterPresence(
  sessions: (HostedAgentSession | null)[],
  runs: AgentRunModel[]
): Map<string, AgentPresence> {
  const activeSessionIds = new Set(
    runs
      .filter((run) => run.status === "executing" || run.status === "synthesizing")
      .map((run) => run.session_id)
  )
  const result = new Map<string, AgentPresence>()
  for (const session of sessions) {
    if (
      session?.hostedAgentId &&
      session.id &&
      session.currentUserRole === "owner" &&
      activeSessionIds.has(session.id)
    )
      result.set(session.hostedAgentId, "working")
  }
  return result
}

/**
 * Per-thread presence for the thread list, from the run the backend stamps on
 * the caller's OWN sessions (`agent_run`): the agent is working, has answered
 * and waits for the user (unread), failed, was stopped, or is simply idle.
 * Foreign (shared) threads carry no run and get no presence — never inferred.
 */
export function threadPresence(session: HostedAgentSession): AgentPresence | null {
  if (session.currentUserRole !== "owner") return null
  const run = session.agent_run
  if (!run?.status) return "idle"
  if (run.status === "executing" || run.status === "synthesizing") return "working"
  if (run.status === "failed") return "blocked"
  if (run.status === "cancelled") return "stopped"
  // "done": the agent has answered — until the user has looked, it is waiting for them.
  return run.unread ? "waiting" : "done"
}

type PresenceInput = {
  state: Pick<
    SessionState,
    | "isHydrating"
    | "isLoading"
    | "isStreaming"
    | "error"
    | "toolApprovals"
    | "toolApprovalSessionId"
    | "durableTurnReconnecting"
    | "cancelPending"
    | "sawMessageSaved"
    | "isAwaitingSave"
    | "messages"
  >
  sessionId: string | null
  aborted: boolean
  run?: AgentRunModel | null
}

/**
 * Same non-empty check `answersForSpreadsheet` uses to keep a streaming bubble.
 * Missing content is empty — `String(undefined)` would be the word "undefined".
 */
function streamingBubbleHasDisplayedContent(content: string | null | undefined): boolean {
  return String(content ?? "").trim().length > 0
}

/**
 * Whether the session-level `error` is the SAME failure the thread already
 * shows. A failed durable turn lands twice in session state: the answer
 * carries `error` (rendered by `system-message.tsx` as the failure card) AND
 * `sessionState.error` is set (what drives the "blocked" presence). The
 * workspace used to render `Chat.Error` for the latter on top of the card, so
 * one failure read as two until a reload. The presence still derives from
 * `error`; only the duplicate card is suppressed.
 *
 * Identity, not mere co-occurrence — Explorer shares this check, and there a
 * new failure must never hide behind an older one:
 *  - streaming partial: when `streamingAnswer` is set, only that partial can
 *    be this failure. It is already the error card when it carries `error`,
 *    its displayed content is non-empty
 *    (`String(content ?? "").trim().length > 0`, the condition that keeps the
 *    bubble in `answersForSpreadsheet`), AND `state.error` equals
 *    `durableTurnErrorText` of that answer's `error` / `error_code`. Empty or
 *    whitespace content is the exception: the page drops that bubble, so this
 *    returns false and `Chat.Error` is the only card. Any other streaming
 *    answer (in flight, or a different error) returns false — it must not hide
 *    a new failure behind an older message.
 *  - no streaming answer: the failed answer must be the thread's latest
 *    message, and the session string must be `durableTurnErrorText` of that
 *    answer's own `error` / `error_code`. A refusal at accept lands as the raw
 *    `ApiError` (never that string), and a later turn's different failure has
 *    different text; both are NEW failures and stay visible.
 */
export function sessionErrorAlreadyInThread(
  state: Pick<SessionState, "error" | "messages"> & Partial<Pick<SessionState, "streamingAnswer">>
): boolean {
  if (typeof state.error !== "string" || !state.error) return false
  if (state.streamingAnswer) {
    const partial = state.streamingAnswer[1]
    return (
      Boolean(partial.error) &&
      streamingBubbleHasDisplayedContent(partial.content) &&
      state.error === durableTurnErrorText({ message: partial.error, code: partial.error_code })
    )
  }
  const latestResponse = state.messages.at(-1)?.[1]
  if (!latestResponse || !("error" in latestResponse) || !latestResponse.error) return false
  return (
    state.error ===
    durableTurnErrorText({ message: latestResponse.error, code: latestResponse.error_code })
  )
}

export type ThreadFailureRecoveryActions = {
  retry: boolean
  continue: boolean
}

/**
 * Retry/Continue for a failure the thread already shows.
 *
 * The session-level error card is the one that carries the handlers, and it is
 * suppressed when this returns a value so the error text is not drawn twice.
 * Null when that card is itself on screen (it already has the buttons), when
 * neither action applies (Retry needs a last question, Continue needs
 * `canContinue`), or when the settled code is `fair_use_blocked` or
 * `plan_upgrade_required` — those refusals must not resend the question.
 * The code is `getSessionErrorCode`, the same source `Chat.Error` reads.
 */
export function threadFailureRecoveryActions(
  state: Pick<
    SessionState,
    "error" | "errorCode" | "streamingAnswer" | "messages" | "canContinue" | "lastQuestion"
  >
): ThreadFailureRecoveryActions | null {
  if (!sessionErrorAlreadyInThread(state)) return null
  const errorCode = getSessionErrorCode(state)
  if (errorCode === "fair_use_blocked" || errorCode === "plan_upgrade_required") return null
  const retry = Boolean(state.lastQuestion[0])
  const continueTurn = Boolean(state.canContinue)
  if (!retry && !continueTurn) return null
  return { retry, continue: continueTurn }
}

/** Local turn state wins over the slower run registry (which may describe an older turn). */
export function agentChatPresence({
  state,
  sessionId,
  aborted,
  run,
}: PresenceInput): AgentPresence {
  if (state.isHydrating) return "loading"
  if (state.error) return "blocked"
  if (state.cancelPending) return "stopping"
  if (aborted) return "stopped"
  if (state.durableTurnReconnecting) return "reconnecting"
  if (
    sessionId &&
    state.toolApprovalSessionId === sessionId &&
    state.toolApprovals.some((approval) => approval.status === "pending")
  )
    return "waiting"
  if (state.isStreaming) return "working"
  if (state.isLoading) return "thinking"
  if (state.isAwaitingSave) return "working"
  const latestResponse = state.messages.at(-1)?.[1]
  if (state.sawMessageSaved) {
    // A persisted partial is a save acknowledgement, not a successful answer.
    if (latestResponse?.interrupted) return "stopped"
    if (latestResponse?.error) return "blocked"
    return "done"
  }
  const matchingRun = run?.session_id === sessionId ? run : null
  const registrySaysWorking =
    matchingRun?.status === "executing" || matchingRun?.status === "synthesizing"
  // `durable_turn` is stamped client-side on the merged final answer (never on
  // a history row), so a latest response carrying it AND a stop/failure marker
  // is a settle THIS tab witnessed — newer than any run-registry row that still
  // says executing. The registry refreshes on the slower session-list cadence,
  // and right after Stop it kept reporting "Arbetar" for up to 13 s between the
  // persisted stopped answer and the list refetch (D7: Stoppar… → Arbetar →
  // Stoppad). A plain completed answer keeps deferring to the registry (a
  // follow-up turn started elsewhere legitimately shows as working); a newer
  // turn otherwise reaches this tab through hydration as isLoading/isStreaming.
  const witnessedStopOrFailure =
    Boolean(latestResponse?.durable_turn) &&
    Boolean(latestResponse?.interrupted || latestResponse?.error)
  if (registrySaysWorking && !witnessedStopOrFailure) return "working"
  // Durable turns do not emit the legacy message_saved acknowledgement.
  // Their persisted response carries explicit terminal/partial markers instead.
  if (latestResponse?.interrupted) return "stopped"
  if (latestResponse?.error) return "blocked"
  if (latestResponse?.completed) return "done"
  // A terminal registry row may belong to a previous turn in this session.
  // Only use it when there is no persisted response outcome to display.
  if (matchingRun?.status === "failed") return "blocked"
  if (matchingRun?.status === "cancelled") return "stopped"
  if (matchingRun?.status === "done") return "done"
  return "idle"
}

/** How often the open thread re-reads the run registry while it still claims a
 * run this tab has already seen settle (#3207). */
export const SETTLED_RUN_RECHECK_MS = 3000

/**
 * The open thread's run-registry row, as far as busy/presence may trust it.
 *
 * The registry is a polled snapshot (30 s while a run is active). A snapshot
 * read BEFORE this tab watched its turn settle cannot describe a newer turn, so
 * its "executing" claim is stale: trusting it kept the presence line on
 * "Working" and the Remove-from-task control disabled after the answer had
 * landed (#3207). Such a row is withheld (null) until the registry is re-read;
 * a snapshot taken after the settle is authoritative again, so a turn another
 * tab starts still shows as working. Terminal rows always pass through.
 */
export function trustedSessionRun(
  run: AgentRunModel | null,
  { snapshotAt, settledAt }: { snapshotAt: number; settledAt: number | null }
): AgentRunModel | null {
  if (!run) return null
  const claimsActive = run.status === "executing" || run.status === "synthesizing"
  if (claimsActive && settledAt !== null && snapshotAt < settledAt) return null
  return run
}
